/**
 * Arma tu kit — Rofuss para Barber Chile (2026-10-02). Lo pinta blocks/arma-tu-kit.liquid.
 *
 * - Tocar una tarjeta la marca o desmarca; el total, el ahorro y el texto del botón se recalculan.
 * - «Este producto» sigue la variante elegida en la ficha (evento estándar shopify:product:select) y,
 *   al agregar, se toma la variante del formulario principal por si cambió sin evento.
 * - El botón agrega todo lo marcado con /cart/add.js y avisa al carrito con CartLinesUpdateEvent,
 *   igual que assets/product-form.js: se actualizan el contador y el carrito lateral, y este se abre solo.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const CHECK =
  '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M1.5 6.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const number = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 });
const money = (cents) => '$' + number.format(Math.round(cents / 100));
const priceHTML = (price, compare) => money(price) + (compare > price ? '<s>' + money(compare) + '</s>' : '');

class ArmaTuKit extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.track = this.querySelector('.rkit__track');
    this.bar = this.querySelector('.rkit__bar i');
    this.button = this.querySelector('.rkit__btn');
    this.error = this.querySelector('.rkit__error');
    try {
      this.variants = JSON.parse(this.querySelector('[data-kit-variants]')?.textContent || '[]');
    } catch (e) {
      this.variants = [];
    }

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.track.addEventListener('scroll', this.updateBar, { passive: true });
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.updateBar);
      this.resizeObserver.observe(this.track);
    }
    document.addEventListener(StandardEvents.productSelect, this.onProductSelect);

    this.update();
    this.updateBar();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.productSelect, this.onProductSelect);
    this.resizeObserver?.disconnect();
    this.ready = false;
  }

  get cards() {
    return Array.from(this.querySelectorAll('.rkit__card'));
  }

  get selected() {
    return this.cards.filter((card) => card.querySelector('.rkit__input')?.checked);
  }

  updateBar = () => {
    const width = this.track.scrollWidth || 1;
    this.bar.style.width = Math.min(100, (this.track.clientWidth / width) * 100) + '%';
    this.bar.style.left = (this.track.scrollLeft / width) * 100 + '%';
  };

  update() {
    const selected = this.selected;
    let total = 0;
    let compare = 0;
    for (const card of selected) {
      const price = Number(card.dataset.price) || 0;
      total += price;
      compare += Math.max(price, Number(card.dataset.compare) || 0);
    }
    for (const card of this.cards) {
      card.classList.toggle('is-on', !!card.querySelector('.rkit__input')?.checked);
    }

    const count = selected.length;
    this.querySelector('.rkit__count').textContent = count + (count === 1 ? ' producto' : ' productos');
    this.querySelector('.rkit__tot strong').textContent = money(total);
    this.querySelector('.rkit__tot s').textContent = compare > total ? money(compare) : '';
    this.querySelector('.rkit__save').textContent = compare > total ? 'Ahorras ' + money(compare - total) : '';

    if (this.busy || this.button.classList.contains('is-added')) return;
    this.button.disabled = count === 0;
    // En celular se oculta «productos» (.rkit__long) y queda «Agregar 3 al carrito»
    this.button.innerHTML = count
      ? '<span>Agregar ' + count + ' <span class="rkit__long">' + (count === 1 ? 'producto' : 'productos') + ' </span>al carrito</span>'
      : 'Marca al menos un producto';
  }

  onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('.rkit__variant')) {
      const option = /** @type {HTMLSelectElement} */ (target).selectedOptions[0];
      const card = target.closest('.rkit__card');
      if (!option || !card) return;
      card.dataset.variantId = option.value;
      card.dataset.price = option.dataset.price || '0';
      card.dataset.compare = option.dataset.compare || '0';
      card.querySelector('.rkit__price').innerHTML = priceHTML(Number(card.dataset.price), Number(card.dataset.compare));
      // Elegir una opción también marca el producto
      const input = card.querySelector('.rkit__input');
      if (input) input.checked = true;
    }

    if (this.error) this.error.hidden = true;
    this.update();
  };

  onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const arrow = target.closest('.rkit__arrow');
    if (arrow) {
      this.track.scrollBy({ left: Number(arrow.dataset.dir) * this.track.clientWidth * 0.8, behavior: 'smooth' });
      return;
    }
    if (target.closest('.rkit__btn')) this.add();
  };

  onProductSelect = (event) => {
    const productId = String(event.product?.id || '');
    if (productId.split('/').pop() !== this.dataset.productId) return;
    event.promise
      ?.then((result) => {
        const raw = result?.detail?.resource?.id ?? result?.variant?.id;
        if (raw) this.setMainVariant(String(raw).split('/').pop());
      })
      .catch(() => {});
  };

  setMainVariant(id) {
    const card = this.querySelector('[data-main]');
    const variant = this.variants.find((v) => String(v.id) === String(id));
    if (!card || !variant || card.dataset.variantId === String(variant.id)) return;

    card.dataset.variantId = String(variant.id);
    card.dataset.price = String(variant.price);
    card.dataset.compare = String(variant.compare || 0);
    card.querySelector('.rkit__price').innerHTML = priceHTML(variant.price, variant.compare || 0);

    const img = card.querySelector('.rkit__img');
    if (img && variant.img) {
      img.removeAttribute('srcset');
      img.setAttribute('src', variant.img);
    }

    const input = card.querySelector('.rkit__input');
    if (input) {
      if (!variant.available) {
        input.checked = false;
        input.disabled = true;
      } else if (input.disabled) {
        input.disabled = false;
        input.checked = true;
      }
    }
    card.classList.toggle('is-disabled', !variant.available);
    this.update();
  }

  /** La variante del formulario principal manda: puede haber cambiado sin evento. */
  syncMainFromForm() {
    const scope = this.closest('.shopify-section') || document;
    const input = scope.querySelector('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]');
    if (input && input.value) this.setMainVariant(input.value);
  }

  async add() {
    if (this.busy) return;
    this.syncMainFromForm();

    const lines = this.selected.map((card) => ({
      id: Number(card.dataset.variantId),
      quantity: 1,
      properties: {
        _origen: 'arma_tu_kit',
        _kit_producto_principal: this.dataset.productTitle || String(this.dataset.productId),
      },
    }));
    if (!lines.length) return;

    this.busy = true;
    this.button.disabled = true;
    this.button.textContent = 'Agregando…';
    if (this.error) this.error.hidden = true;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    let response = null;
    let didError = false;
    let message = '';
    try {
      const res = await fetch(this.dataset.addUrl || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
      });
      response = await res.json();
      if (!res.ok || response?.status) {
        didError = true;
        message = response?.description || response?.message || '';
      }
    } catch (e) {
      didError = true;
    }

    try {
      const cart = await fetch(this.dataset.cartUrl || '/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }).then((res) => res.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'arma-tu-kit',
          sourceId: this.dataset.blockId,
          itemCount: lines.length,
          productId: this.dataset.productId,
          sections: didError ? undefined : response?.sections,
          didError,
        },
      });
    } catch (e) {
      deferred.reject(e);
    }

    this.busy = false;

    if (didError) {
      if (this.error) {
        this.error.textContent = message || 'No se pudo agregar. Intenta de nuevo.';
        this.error.hidden = false;
      }
      this.update();
      return;
    }

    this.button.disabled = false;
    this.button.classList.add('is-added');
    this.button.innerHTML = CHECK + 'Agregados al carrito';
    setTimeout(() => {
      this.button.classList.remove('is-added');
      this.update();
    }, 2500);
  }
}

if (!customElements.get('arma-tu-kit')) customElements.define('arma-tu-kit', ArmaTuKit);
